import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { HiOutlineArrowDownTray, HiOutlineDocumentText, HiOutlineMagnifyingGlassMinus, HiOutlineMagnifyingGlassPlus } from "react-icons/hi2";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";

import { Button } from "../../shared";
import { PREVIEW_MAX_BYTES, formatBytes, getPreviewKind } from "../previewUtils";
import axiosInstance from "../../../redux/network/https";

const PdfPreview = ({ url }) => {
  const handleOpen = () => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
      <HiOutlineDocumentText className="h-16 w-16 text-grey/40" />
      <div>
        <p className="text-lg font-semibold text-darkgrey">PDF document</p>
        <p className="mt-1 text-sm text-grey">Inline preview for PDFs is not available.</p>
      </div>
      <Button variant="filled" color="secondary" onClick={handleOpen} icon={HiOutlineArrowDownTray}>
        Open in new tab
      </Button>
    </div>
  );
};

const ImagePreview = ({ url }) => {
  const [scale, setScale] = useState(1);
  const [error, setError] = useState(null);

  if (error) {
    return <PreviewError message={error} />;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-1 items-center justify-center overflow-auto bg-surface p-4 min-h-0">
        <img
          src={url}
          alt="Preview"
          className="max-h-full max-w-full object-contain transition-transform duration-200"
          style={{ transform: `scale(${scale})` }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
            setError("Failed to load preview");
          }}
        />
      </div>
      <div className="flex items-center justify-end gap-1.5 border-t border-surface-border bg-white px-3 py-2">
        <button
          onClick={() => setScale((s) => Math.max(s - 0.25, 0.25))}
          className="rounded-md p-1.5 text-grey transition-colors hover:bg-surface-active hover:text-darkgrey"
          aria-label="Zoom out"
        >
          <HiOutlineMagnifyingGlassMinus className="h-4 w-4" />
        </button>
        <button
          onClick={() => setScale(1)}
          className="rounded px-2 py-1 text-xs font-medium text-darkgrey transition-colors hover:text-primary"
          aria-label="Reset zoom"
        >
          {Math.round(scale * 100)}%
        </button>
        <button
          onClick={() => setScale((s) => Math.min(s + 0.25, 4))}
          className="rounded-md p-1.5 text-grey transition-colors hover:bg-surface-active hover:text-darkgrey"
          aria-label="Zoom in"
        >
          <HiOutlineMagnifyingGlassPlus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const TextPreview = ({ url, contentUrl }) => {
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setContent(null);
    setError(null);

    // Prefer the same-origin backend content proxy (avoids S3 CORS blocks on
    // browser fetch). Fall back to the presigned URL when no proxy is provided.
    const source = contentUrl || url;
    const ac = new AbortController();

    const load = async () => {
      try {
        if (contentUrl) {
          const res = await axiosInstance.get(source, {
            responseType: "text",
            signal: ac.signal,
          });
          setContent(res.data);
        } else {
          const res = await fetch(source, { signal: ac.signal });
          if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
          setContent(await res.text());
        }
      } catch (e) {
        // AbortController cancellations: native fetch throws AbortError,
        // axios throws CanceledError (code ERR_CANCELED).
        if (e.name !== "AbortError" && e.code !== "ERR_CANCELED") setError("Failed to load preview");
      }
    };

    load();
    return () => ac.abort();
  }, [url, contentUrl]);

  if (error) return <p className="p-6 text-sm text-danger">{error}</p>;
  if (content === null) return <p className="p-6 text-sm text-grey">Loading...</p>;

  return (
    <pre className="h-full overflow-auto whitespace-pre-wrap break-words bg-surface p-4 font-mono text-sm text-darkgrey">
      {content}
    </pre>
  );
};

const MarkdownPreview = ({ url, contentUrl }) => {
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setContent(null);
    setError(null);

    const source = contentUrl || url;
    const ac = new AbortController();

    const load = async () => {
      try {
        if (contentUrl) {
          const res = await axiosInstance.get(source, {
            responseType: "text",
            signal: ac.signal,
          });
          setContent(res.data);
        } else {
          const res = await fetch(source, { signal: ac.signal });
          if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
          setContent(await res.text());
        }
      } catch (e) {
        if (e.name !== "AbortError" && e.code !== "ERR_CANCELED") setError("Failed to load preview");
      }
    };

    load();
    return () => ac.abort();
  }, [url, contentUrl]);

  if (error) return <p className="p-6 text-sm text-danger">{error}</p>;
  if (content === null) return <p className="p-6 text-sm text-grey">Loading...</p>;

  return (
    <div className="h-full overflow-auto bg-surface p-6 text-sm leading-relaxed text-darkgrey">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          h1: ({ children, ...props }) => (
            <h1 className="mb-3 mt-5 text-2xl font-semibold text-darkgrey first:mt-0" {...props}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 className="mb-2 mt-4 text-xl font-semibold text-darkgrey first:mt-0" {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 className="mb-2 mt-4 text-lg font-semibold text-darkgrey first:mt-0" {...props}>
              {children}
            </h3>
          ),
          p: ({ children, ...props }) => (
            <p className="mb-3 last:mb-0" {...props}>
              {children}
            </p>
          ),
          a: ({ children, ...props }) => (
            <a className="text-secondary-dark underline underline-offset-2 hover:text-primary" target="_blank" rel="noreferrer noopener" {...props}>
              {children}
            </a>
          ),
          ul: ({ children, ...props }) => (
            <ul className="mb-3 list-disc space-y-1 pl-6 last:mb-0" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="mb-3 list-decimal space-y-1 pl-6 last:mb-0" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="marker:text-grey" {...props}>
              {children}
            </li>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote className="mb-3 border-l-4 border-surface-border pl-4 italic text-grey last:mb-0" {...props}>
              {children}
            </blockquote>
          ),
          table: ({ children, ...props }) => (
            <div className="mb-4 overflow-x-auto">
              <table className="min-w-full border-collapse text-left text-sm" {...props}>
                {children}
              </table>
            </div>
          ),
          th: ({ children, ...props }) => (
            <th className="border border-surface-border bg-white px-3 py-2 font-semibold text-darkgrey" {...props}>
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td className="border border-surface-border px-3 py-2 align-top" {...props}>
              {children}
            </td>
          ),
          code: ({ inline, children, ...props }) =>
            inline ? (
              <code className="rounded bg-surface-hover px-1 py-0.5 font-mono text-[0.875em] text-darkgrey" {...props}>
                {children}
              </code>
            ) : (
              <code className="font-mono text-sm text-darkgrey" {...props}>
                {children}
              </code>
            ),
          pre: ({ children, ...props }) => (
            <pre className="mb-3 overflow-x-auto rounded-xl bg-surface-hover p-4 text-sm text-darkgrey last:mb-0" {...props}>
              {children}
            </pre>
          ),
          hr: () => <hr className="my-4 border-surface-border" />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

const PreviewError = ({ message, onDownload }) => (
  <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
    <HiOutlineDocumentText className="h-16 w-16 text-grey/40" />
    <div>
      <p className="text-lg font-semibold text-darkgrey">Preview unavailable</p>
      <p className="mt-1 text-sm text-grey">{message}</p>
    </div>
    {onDownload && (
      <Button variant="filled" color="secondary" onClick={onDownload} icon={HiOutlineArrowDownTray}>
        Download
      </Button>
    )}
  </div>
);

const UnsupportedPreview = ({ file, onDownload }) => (
  <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
    <HiOutlineDocumentText className="h-16 w-16 text-grey/40" />
    <div>
      <p className="text-lg font-semibold text-darkgrey">{file?.original_name}</p>
      <p className="mt-1 text-sm text-grey">{formatBytes(file?.size_bytes)}</p>
      <p className="mt-3 text-sm text-grey">Preview not available for this file type</p>
    </div>
    {onDownload && (
      <Button variant="filled" color="secondary" onClick={onDownload} icon={HiOutlineArrowDownTray}>
        Download
      </Button>
    )}
  </div>
);

const TooLargePreview = ({ file, onDownload }) => (
  <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
    <HiOutlineDocumentText className="h-16 w-16 text-grey/40" />
    <div>
      <p className="text-lg font-semibold text-darkgrey">{file?.original_name}</p>
      <p className="mt-1 text-sm text-grey">{formatBytes(file?.size_bytes)}</p>
      <p className="mt-3 text-sm text-grey">File too large to preview</p>
    </div>
    {onDownload && (
      <Button variant="filled" color="secondary" onClick={onDownload} icon={HiOutlineArrowDownTray}>
        Download instead
      </Button>
    )}
  </div>
);

export const FilePreviewSurface = ({ file, previewUrl, contentUrl, onDownload, error }) => {
  const kind = getPreviewKind(file);
  const isTooLarge = file?.size_bytes > PREVIEW_MAX_BYTES;

  if (error && !previewUrl) {
    return <PreviewError message={error} onDownload={onDownload} />;
  }

  if (kind === "unsupported") {
    return <UnsupportedPreview file={file} onDownload={onDownload} />;
  }

  if (isTooLarge) {
    return <TooLargePreview file={file} onDownload={onDownload} />;
  }

  if (!previewUrl && !contentUrl) {
    return <p className="p-6 text-sm text-grey">Loading preview...</p>;
  }

  if (kind === "image") {
    return <ImagePreview url={previewUrl} />;
  }

  if (kind === "pdf") {
    return <PdfPreview url={previewUrl} />;
  }

  if (kind === "text") {
    return <TextPreview url={previewUrl} contentUrl={contentUrl} />;
  }

  if (kind === "markdown") {
    return <MarkdownPreview url={previewUrl} contentUrl={contentUrl} />;
  }

  return <UnsupportedPreview file={file} onDownload={onDownload} />;
};
